import type { Category } from '@/types'
import { debugError } from '@/utils/env'
import type { ApiResponse } from './types'

// API基础URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// 分类信息接口
export interface CategoryInfo {
  id: number
  name: string
  slug: string
  description: string
  color: string
  created_at: string
}

/**
 * 获取所有分类列表
 */
export async function getCategories(): Promise<CategoryInfo[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/categories`)
    const result: ApiResponse<CategoryInfo[]> = await response.json()

    if (result.errorcode === '0') {
      return result.data
    } else {
      throw new Error(result.errormsg || '获取分类列表失败')
    }
  } catch (error) {
    debugError('获取分类列表失败:', error)
    // 返回默认分类数据作为降级方案
    return getDefaultCategories()
  }
}

/**
 * 根据slug获取单个分类信息
 */
export async function getCategoryBySlug(slug: string): Promise<CategoryInfo | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/categories/${slug}`)
    const result: ApiResponse<CategoryInfo> = await response.json()

    if (result.errorcode === '0') {
      return result.data
    } else {
      throw new Error(result.errormsg || '获取分类信息失败')
    }
  } catch (error) {
    debugError('获取分类信息失败:', error)
    return null
  }
}

/**
 * 获取分类下的纪录片列表
 */
export async function getCategoryDocumentaries(
  slug: string,
  page: number = 1,
  limit: number = 20
): Promise<{ documentaries: any[], total: number }> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/categories/${slug}/documentaries?page=${page}&limit=${limit}`
    )
    const result: ApiResponse<{ documentaries: any[], total: number }> = await response.json()

    if (result.errorcode === '0') {
      return result.data
    } else {
      throw new Error(result.errormsg || '获取分类纪录片失败')
    }
  } catch (error) {
    debugError('获取分类纪录片失败:', error)
    return { documentaries: [], total: 0 }
  }
}

/**
 * 默认分类数据（降级方案）
 */
function getDefaultCategories(): CategoryInfo[] {
  return [
    {
      id: 1,
      name: '自然',
      slug: 'nature',
      description: '探索自然界的奇观与野生动物',
      color: '#22C55E',
      created_at: new Date().toISOString()
    },
    {
      id: 2,
      name: '历史',
      slug: 'history',
      description: '回顾人类历史的重要时刻与文明',
      color: '#8B5CF6',
      created_at: new Date().toISOString()
    },
    {
      id: 3,
      name: '科学',
      slug: 'science',
      description: '揭示科学原理与技术进步',
      color: '#3B82F6',
      created_at: new Date().toISOString()
    },
    {
      id: 4,
      name: '文化',
      slug: 'culture',
      description: '了解世界各地的文化与艺术',
      color: '#F59E0B',
      created_at: new Date().toISOString()
    },
    {
      id: 5,
      name: '社会',
      slug: 'society',
      description: '关注社会现象与人文关怀',
      color: '#EF4444',
      created_at: new Date().toISOString()
    },
    {
      id: 6,
      name: '传记',
      slug: 'biography',
      description: '讲述杰出人物的生平故事',
      color: '#06B6D4',
      created_at: new Date().toISOString()
    },
    {
      id: 7,
      name: '旅行',
      slug: 'travel',
      description: '探索世界各地的风土人情',
      color: '#10B981',
      created_at: new Date().toISOString()
    },
    {
      id: 8,
      name: '美食',
      slug: 'food',
      description: '品味全球美食文化',
      color: '#F97316',
      created_at: new Date().toISOString()
    }
  ]
}

/**
 * 分类信息缓存
 */
let categoryCache: CategoryInfo[] | null = null
let cacheTimestamp: number = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5分钟缓存

/**
 * 获取分类列表（带缓存）
 */
export async function getCategoriesWithCache(): Promise<CategoryInfo[]> {
  const now = Date.now()
  
  // 如果缓存存在且未过期，直接返回
  if (categoryCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return categoryCache
  }
  
  // 否则重新获取数据
  const categories = await getCategories()
  categoryCache = categories
  cacheTimestamp = now
  
  return categories
}

/**
 * 根据slug获取分类名称
 */
export async function getCategoryName(slug: string): Promise<string> {
  const categories = await getCategoriesWithCache()
  const category = categories.find(c => c.slug === slug)
  return category?.name || slug.toUpperCase()
}

/**
 * 根据slug获取分类描述
 */
export async function getCategoryDescription(slug: string): Promise<string> {
  const categories = await getCategoriesWithCache()
  const category = categories.find(c => c.slug === slug)
  return category?.description || ''
}

/**
 * 根据slug获取分类颜色
 */
export async function getCategoryColor(slug: string): Promise<string> {
  const categories = await getCategoriesWithCache()
  const category = categories.find(c => c.slug === slug)
  return category?.color || '#666666'
}

/**
 * 获取所有分类的选项列表（用于下拉菜单等）
 */
export async function getCategoryOptions(): Promise<{ value: Category; label: string }[]> {
  const categories = await getCategoriesWithCache()
  return categories.map(category => ({
    value: category.slug as Category,
    label: category.name
  }))
}

/**
 * 清除分类缓存
 */
export function clearCategoryCache(): void {
  categoryCache = null
  cacheTimestamp = 0
}